import { RefObject, useEffect } from 'react';
import { findScreenEntryTarget, SCREEN_CONTENT_SELECTOR } from '../helpers/screenEntryFocus';
import { getTabbableElements } from '../helpers/tabbableHelper';

const POINTER_ENTRY_MS = 1000;

/** office.js enters the pane on Tab, Shift+Tab and Ctrl+F6 by focusing its first or last tabbable. */
const isAtPageEdge = (element: Element) => {
  const tabbables = getTabbableElements(document.body);
  return element === tabbables[0] || element === tabbables[tabbables.length - 1];
};

export const usePaneEntryFocus = (layoutRef: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    let pointerDownAt = Number.NEGATIVE_INFINITY;
    let focusWhenLeft: Element | null = null;
    let frame = 0;

    const notePointerDown = () => {
      pointerDownAt = performance.now();
    };
    const noteFocusWhenLeft = () => {
      focusWhenLeft = document.activeElement;
    };

    const isFreshEntry = (layout: HTMLElement, active: Element) => {
      if (active === document.body) return true;
      if (active === focusWhenLeft) return false;
      if (!layout.contains(active)) return false;
      return isAtPageEdge(active);
    };

    const sendEntryToScreenStart = () => {
      const layout = layoutRef.current;
      const active = document.activeElement;
      if (!layout || !active || !isFreshEntry(layout, active)) return;
      const content = layout.querySelector<HTMLElement>(SCREEN_CONTENT_SELECTOR) ?? layout;
      findScreenEntryTarget(content)?.focus();
    };

    const onPaneFocus = () => {
      if (performance.now() - pointerDownAt < POINTER_ENTRY_MS) return;
      cancelAnimationFrame(frame);
      // office.js focuses its first tabbable right after focusing the window.
      frame = requestAnimationFrame(sendEntryToScreenStart);
    };

    window.addEventListener('focus', onPaneFocus);
    window.addEventListener('blur', noteFocusWhenLeft);
    document.addEventListener('pointerdown', notePointerDown, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('focus', onPaneFocus);
      window.removeEventListener('blur', noteFocusWhenLeft);
      document.removeEventListener('pointerdown', notePointerDown, true);
    };
  }, [layoutRef]);
};
